<script setup lang="ts">
/**
 * Список задач проекта — макет 11:295: таблица со строками 40, группами по
 * статусу и пагинацией.
 *
 * Второй вид того же проекта: доска отвечает на «что в работе», список — на
 * «покажи всё разом и дай отсортировать». Отбор и кнопка создания в панели те
 * же, что на доске, — переключение вида не должно ощущаться переходом в другое
 * приложение.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { AlertTriangle, ChevronDown, ChevronRight, ChevronsUpDown, ListTodo, SearchX } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import InlinePicker from '@/components/InlinePicker.vue'
import IssueFilterBar from '@/components/IssueFilterBar.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ViewSwitch from '@/components/ViewSwitch.vue'
import IssuePeek from '@/features/issue/components/IssuePeek.vue'
import BulkActionsBar from '@/features/issues/components/BulkActionsBar.vue'
import CreateIssueModal from '@/features/issues/components/CreateIssueModal.vue'
import IssueTableRow from '@/features/issues/components/IssueTableRow.vue'
import { emptyFilters } from '@/features/issues/filters'
import { GROUP_OPTIONS, type GroupBy, type IssueGroup } from '@/features/issues/grouping'
import { useIssuesStore } from '@/features/issues/store'
import { listMembers, type ProjectMember } from '@/features/projects/api'

import type { IssueSortField } from '@/features/issues/api'
import type { CreatedIssue } from '@/features/issues/api'

/** Сколько номеров страниц показывать вокруг текущей */
const PAGE_WINDOW = 5

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const store = useIssuesStore()

const projectKey = computed(() => String(route.params.key ?? ''))

const breadcrumbs = computed(() => [
  { label: store.project?.name || projectKey.value, to: '/projects' },
  { label: t('views.issues') },
])

const members = ref<ProjectMember[]>([])

const openList = async () => {
  await store.open(projectKey.value)

  // «Нет такого проекта» и «есть, но не для вас» — разные экраны, и различает
  // их только ответ сервера.
  const failureRoute = errorRoute(store.error, projectKey.value)

  if (failureRoute) {
    await router.replace(failureRoute)

    return
  }

  // Участники нужны только выпадашке исполнителя: отказ здесь оставляет её
  // пустой и не мешает списку.
  members.value = store.projectId ? await listMembers(store.projectId).catch(() => []) : []
}

const groupOptions = computed(() =>
  GROUP_OPTIONS.map((value) => ({ value, label: t(`issues.list.groupBy.${value}`) })),
)

const groupCaption = computed(() =>
  store.grouping === 'none'
    ? t('issues.list.groupBy.label')
    : `${t('issues.list.groupBy.label')}: ${t(`issues.list.groupBy.${store.grouping}`)}`,
)

/** Свёрнутые группы — состояние экрана, а не данных: перезагрузка их забывает */
const collapsed = ref<string[]>([])

const toggleGroup = (id: string) => {
  collapsed.value = collapsed.value.includes(id)
    ? collapsed.value.filter((entry) => entry !== id)
    : [...collapsed.value, id]
}

/**
 * Имя группы знает экран, а не стор: у колонки оно приходит с сервера, у
 * приоритета и типа лежит в переводах, а у исполнителя — в самой задаче.
 */
const groupName = (group: IssueGroup): string => {
  switch (store.grouping) {
    case 'status':
      return store.columnOf(group.id)?.name ?? t('issues.list.noStatus')
    case 'assignee':
      return group.issues[0]?.assigneeName || t('issues.list.noAssignee')
    case 'priority':
      return group.id ? t(`issues.priorities.${group.id}`) : t('issues.list.noPriority')
    case 'type':
      return group.id ? t(`issues.types.${group.id}`) : t('issues.list.noType')
    default:
      return ''
  }
}

/** Заголовки в порядке колонок; пустой ключ — колонка, по которой не сортируют */
const columns: { key: IssueSortField | ''; label: string }[] = [
  { key: '', label: 'type' },
  { key: 'key', label: 'key' },
  { key: '', label: 'title' },
  { key: '', label: 'status' },
  { key: 'priority', label: 'priority' },
  { key: '', label: 'assignee' },
  { key: 'due_date', label: 'due' },
]

const onSort = (field: IssueSortField | '') => {
  if (field) void store.applySort(field)
}

/** Номера страниц: окно вокруг текущей — на сорока страницах все не нужны */
const pageNumbers = computed(() => {
  const last = store.pageCount
  const half = Math.floor(PAGE_WINDOW / 2)
  const start = Math.max(1, Math.min(store.page - half, last - PAGE_WINDOW + 1))

  return Array.from({ length: Math.min(PAGE_WINDOW, last) }, (_, index) => start + index)
})

const onMove = async (id: string, columnId: string) => {
  try {
    await store.moveTo(id, columnId)
  } catch (failure) {
    toast.error(errorMessage(failure, t('board.moveFailed')))
  }
}

const onAssign = async (id: string, assigneeId: string, assigneeName: string) => {
  try {
    await store.assignTo(id, assigneeId, assigneeName)
  } catch (failure) {
    toast.error(errorMessage(failure, t('issues.list.assignFailed')))
  }
}

/** Массовые действия идут по одной задаче: пачечной ручки у сервиса нет. */
const isBulkBusy = ref(false)

const onBulkMove = async (columnId: string) => {
  isBulkBusy.value = true

  try {
    await store.moveSelected(columnId)
  } catch (failure) {
    toast.error(errorMessage(failure, t('board.moveFailed')))
  } finally {
    isBulkBusy.value = false
  }
}

const onBulkAssign = async (assigneeId: string, assigneeName: string) => {
  isBulkBusy.value = true

  try {
    await store.assignSelected(assigneeId, assigneeName)
  } catch (failure) {
    toast.error(errorMessage(failure, t('issues.list.assignFailed')))
  } finally {
    isBulkBusy.value = false
  }
}

const isCreateOpen = ref(false)

const onCreated = async (issue: CreatedIssue) => {
  toast.success(t('issues.create.created', { key: issue.key }))

  await store.load()
}

onMounted(openList)

// Переход между проектами из сайдбара меняет только параметр маршрута — экран
// при этом не пересоздаётся.
watch(projectKey, openList)

/** Отказ правки из панели показываем тем же тостом, что и остальные */
const onPeekFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('issue.saveFailed')))
}
</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template v-if="store.project">
      <ViewSwitch :project-key="store.project.key" />

      <IssueFilterBar
        :filters="store.filters"
        :project-id="store.project.id"
        :type-profile="store.project.typeProfile"
        @change="store.applyFilters"
      />

      <InlinePicker
        :options="groupOptions"
        :model-value="store.grouping"
        @update:model-value="store.setGrouping($event as GroupBy)"
      >
        <template #trigger="{ isOpen, toggle }">
          <button type="button" class="gchip" :aria-expanded="isOpen" @click="toggle()">
            {{ groupCaption }}
            <ChevronDown :size="14" />
          </button>
        </template>
      </InlinePicker>

      <AppButton variant="primary" size="small" @click="isCreateOpen = true">
        {{ t('toolbar.create') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <div class="ilist">
    <div v-if="store.isLoading && !store.issues.length" class="itable">
      <div v-for="index in 8" :key="index" class="itable__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="store.error"
      :icon="AlertTriangle"
      :title="t('issues.list.error.title')"
      :description="t('issues.list.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="store.load">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="store.isEmpty && store.isFiltered"
      :icon="SearchX"
      :title="t('board.nothingFound.title')"
      :description="t('board.nothingFound.description')"
    >
      <AppButton variant="secondary" @click="store.applyFilters(emptyFilters())">
        {{ t('toolbar.filters.reset') }}
      </AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="store.isEmpty"
      :icon="ListTodo"
      :title="t('issues.list.empty.title')"
      :description="t('issues.list.empty.description')"
    >
      <AppButton variant="primary" @click="isCreateOpen = true">
        {{ t('issues.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <template v-else>
      <!-- Панель действий появляется над таблицей, как только что-то отмечено:
           в макете чекбоксы есть, а действий над ними нет (DEV-1501). -->
      <BulkActionsBar
        v-if="store.selected.length"
        class="issues__bulk"
        :count="store.selected.length"
        :columns="store.columns"
        :members="members"
        :busy="isBulkBusy"
        @move="onBulkMove"
        @assign="onBulkAssign"
        @clear="store.selected = []"
      />

      <div class="itable">
        <div class="itable__head">
          <input
            class="itable__check"
            type="checkbox"
            :checked="store.selected.length === store.issues.length"
            :aria-label="t('issues.list.selectAll')"
            @change="store.toggleAll"
          />

          <component
            :is="column.key ? 'button' : 'span'"
            v-for="column in columns"
            :key="column.label"
            :type="column.key ? 'button' : undefined"
            class="itable__h"
            :class="{ 'itable__h--sortable': column.key }"
            @click="onSort(column.key)"
          >
            {{ t(`issues.list.columns.${column.label}`) }}

            <template v-if="column.key">
              <ChevronDown
                v-if="store.sort.field === column.key && !store.sort.desc"
                :size="12"
              />
              <ChevronRight
                v-else-if="store.sort.field === column.key"
                class="itable__sort-up"
                :size="12"
              />
              <ChevronsUpDown v-else class="itable__sort-idle" :size="12" />
            </template>
          </component>
        </div>

        <template v-for="group in store.groups" :key="group.id">
          <button
            v-if="store.grouping !== 'none'"
            type="button"
            class="itable__group"
            :aria-expanded="!collapsed.includes(group.id)"
            @click="toggleGroup(group.id)"
          >
            <ChevronDown v-if="!collapsed.includes(group.id)" :size="14" />
            <ChevronRight v-else :size="14" />
            <span class="itable__group-name">{{ groupName(group) }}</span>
            <span class="itable__group-count">{{ group.issues.length }}</span>
          </button>

          <IssueTableRow
            v-for="issue in collapsed.includes(group.id) ? [] : group.issues"
            :key="issue.id"
            :issue="issue"
            :columns="store.columns"
            :members="members"
            :is-selected="store.selected.includes(issue.id)"
            @toggle="store.toggleSelected"
            @move="onMove"
            @assign="onAssign"
          />
        </template>
      </div>

      <footer class="ifoot">
        <span class="ifoot__shown">
          {{
            store.selected.length
              ? t('issues.list.selected', { count: store.selected.length })
              : t('issues.list.shown', { shown: store.issues.length, total: store.total })
          }}
        </span>

        <button
          v-if="store.selected.length"
          type="button"
          class="ifoot__clear"
          @click="store.selected = []"
        >
          {{ t('issues.list.clearSelection') }}
        </button>

        <nav v-if="store.pageCount > 1" class="ifoot__pages" :aria-label="t('issues.list.pages')">
          <button
            type="button"
            class="ifoot__page"
            :disabled="store.page === 1"
            :aria-label="t('issues.list.prev')"
            @click="store.setPage(store.page - 1)"
          >
            ‹
          </button>

          <button
            v-for="number in pageNumbers"
            :key="number"
            type="button"
            class="ifoot__page"
            :class="{ 'ifoot__page--active': number === store.page }"
            @click="store.setPage(number)"
          >
            {{ number }}
          </button>

          <button
            type="button"
            class="ifoot__page"
            :disabled="store.page === store.pageCount"
            :aria-label="t('issues.list.next')"
            @click="store.setPage(store.page + 1)"
          >
            ›
          </button>
        </nav>
      </footer>
    </template>
  </div>

  <CreateIssueModal
    v-if="store.project"
    v-model="isCreateOpen"
    :project-id="store.project.id"
    :type-profile="store.project.typeProfile"
    column-id=""
    @created="onCreated"
  />

  <IssuePeek @failed="onPeekFailed" />
</template>

<style scoped>
.ilist {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  padding: 20px 24px;
  overflow-y: auto;
}

.issues__bulk {
  margin-bottom: 12px;
}

.itable {
  /* Сетка из макета: отступы 16 по краям, шаг 12, гибкое только название.
     Колонка пайплайна ждёт данных — её 130 отданы названию. */
  --itable-columns: 40px 32px 88px minmax(0, 1fr) 120px 40px 48px 88px;

  display: flex;
  flex-direction: column;
  /* Таблица не ужимается под остаток экрана — она его перерастает, и лишнее
     уходит в прокрутку страницы. Без этого строки сплющиваются до 28. */
  flex-shrink: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: hidden;
}

.itable__head {
  display: grid;
  grid-template-columns: var(--itable-columns);
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  height: 36px;
  padding: 0 16px;
}

.itable__check {
  /* У флажка свой браузерный margin, и без сброса колонка съезжает на 4px
     вправо — заголовок с ней вместе, а строка ниже уже нет. */
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.itable__h {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.04em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.itable__h--sortable {
  cursor: pointer;
}

.itable__h--sortable:hover {
  color: var(--color-text-secondary);
}

/* Направление сортировки — тем же шевроном, повёрнутым вверх: две разные
   иконки на одном месте читаются как два разных элемента. */
.itable__sort-up {
  transform: rotate(-90deg);
}

.itable__sort-idle {
  opacity: 0.5;
}

.itable__group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 16px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.itable__group-name {
  font-weight: var(--font-weight-medium);
}

.itable__group-count {
  color: var(--color-text-muted);
}

.itable__skeleton {
  flex-shrink: 0;
  height: 40px;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
}

.ifoot {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  height: 36px;
  margin-top: 4px;
  padding: 0 4px;
}

.ifoot__shown {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.ifoot__clear {
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.ifoot__pages {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.ifoot__page {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.ifoot__page:hover:not(:disabled) {
  background: var(--color-bg-muted);
}

.ifoot__page:disabled {
  color: var(--color-text-muted);
  cursor: default;
  opacity: 0.5;
}

.ifoot__page--active {
  border-color: var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
}

/* Чип группировки повторяет фильтр-чипы панели: они стоят в одном ряду, и
   разный рост или радиус между ними заметнее, чем кажется. */
.gchip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.gchip:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}
</style>
